import { useNavigate } from "react-router-dom";
import { useQuery } from 'react-query'
import useAtomWithUrlParam from '../hooks/useAtomWithUrlParam';
import { versionAtom, versionAtomDefault } from '../atoms';
import { getHostname } from "../utility/hostname";

const LOCAL_KEY = 'api-key';
const LOCAL_VERSION_CONFIGS = 'version-configs'


function useMephistoReview({
  taskId,
} = {}) {
  const navigate = useNavigate();
  const hostname = getHostname();
  const [version, setVersion] = useAtomWithUrlParam(versionAtom, versionAtomDefault);

  const VERSION_LIST_URL = `${hostname}/versions`;

  const VERSION_CONFIG_URL = `${hostname}/version_config/${version}`;

  const DATA_URL = taskId
    ? `${hostname}/data/${version}/${taskId}`
    : `${hostname}/data/${version}`;

  const FEEDBACK_URL = taskId
    ? `${hostname}/feedback/${version}/${taskId}`
    : `${hostname}/feedback/${version}`;

  const SEARCH_URL = `${hostname}/search/${version}`;

  const API_KEY_HEADER = { 'x-api-key': localStorage.getItem(LOCAL_KEY) }

  const { isLoading, error, data } = useQuery(`${version}-${taskId ?? 'home'}`, () =>
    fetch(DATA_URL, { method: "GET", headers: API_KEY_HEADER })
      .then((res) => {
        if (res.status === 401) {
          // Fetch the list of valid versions and update the version state
          fetch(VERSION_LIST_URL, { method: "GET", headers: API_KEY_HEADER })
            .then(res => {
              if (res.status === 401) {
                navigate('/login');
                return Promise.resolve();
              }
              return res.json();
            })
            .then(data => {
              if (data.versions.length === 0) {
                navigate('/login');
                return Promise.resolve();
              }
              const lastVersion = data.versions[data.versions.length - 1];
              setVersion(lastVersion.name);
              return Promise.resolve();
            })
            .catch(e => console.log(e));
        }
        return res.json();
      }),
    { refetchOnWindowFocus: false }
  );

  const submitFeedback = (feedback) =>
    fetch(FEEDBACK_URL, {
      method: "POST",
      headers: { ...API_KEY_HEADER, 'Content-Type': 'application/json' },
      body: JSON.stringify(feedback),
    });

  const useSearch = (query) =>
    useQuery(
      ['query', query],
      ({ queryKey }) => {
        return fetch(`${SEARCH_URL}?query=${encodeURIComponent(queryKey[1])}`, {
          method: "GET",
          headers: API_KEY_HEADER,
        }).then((res) => {
          return res.json();
        })
      },
      {
        refetchOnWindowFocus: false,
        enabled: false,
        // staleTime: Infinity,
      }
    );

  const { data: versionList } = useQuery('versions', () =>
    fetch(VERSION_LIST_URL, { method: "GET", headers: API_KEY_HEADER })
      .catch((e) => {
        console.log(e);
      })
      .then((res) => {
        if (res.status === 401) {
          navigate('/login');
        }
        return res.json();
      }),
    { refetchOnWindowFocus: false }
  );

  const { data: versionConfig } = useQuery(`${version}-config`, () =>
    fetch(VERSION_CONFIG_URL, { method: "GET", headers: API_KEY_HEADER })
      .catch((e) => {
        console.log(e)
      })
      .then((res) => {
        return res.json()
      })
      .then((res) => {
        res['filter_options'] = res['filter_options'].map((o) => {
          return {
            ...o,
            ...(o.operators) && { customOperatorFunc: (_category) => o.operators },
            ...(o.values) && { customValuesFunc: (_c, _o) => o.values },
          }
        })
        localStorage.setItem(LOCAL_VERSION_CONFIGS, {
          ...(localStorage.getItem(LOCAL_VERSION_CONFIGS) ?? {}),
          version: res
        })
        return res
      })
  )


  return {
    data: data && data.data,
    error: error,
    filterData: data && data.filter_data,
    isLoading: isLoading,
    versionConfig: versionConfig ?? (localStorage.getItem(LOCAL_VERSION_CONFIGS) ?? {})[version],
    versionList: versionList && versionList.versions,
    useSearch: useSearch,
    submitFeedback: submitFeedback,
    totalPages: (data && data.total_pages) || 1,
  };
}

export { useMephistoReview };
